
import * as React from 'react';
const { useState, useEffect, useCallback } = React;
import { NetworkStatus } from '@/types';

const NETWORK_CHECK_INTERVAL = 30000; // 30 seconds
const NETWORK_STATUS_TTL = 60000; // 1 minute

interface NetworkInformation extends EventTarget {
  effectiveType: string;
  downlink: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

export function useNetworkStatus() {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    type: 'fast',
    lastChecked: Date.now(),
    effectiveType: 'unknown',
  });

  const checkNetworkStatus = useCallback(async () => {
    const now = Date.now();
    
    // Return cached status if within TTL
    if (now - networkStatus.lastChecked < NETWORK_STATUS_TTL) {
      return networkStatus;
    }

    const connection = (navigator as NavigatorWithConnection).connection;
    const isSlowConnection = connection ? (
      connection.effectiveType === 'slow-2g' || 
      connection.effectiveType === '2g' || 
      connection.effectiveType === '3g' ||
      (connection.effectiveType === '4g' && connection.downlink < 1.5)
    ) : false;

    const newStatus: NetworkStatus = {
      type: isSlowConnection ? 'slow' : 'fast',
      lastChecked: now,
      downlink: connection?.downlink,
      effectiveType: connection?.effectiveType,
      rtt: connection?.rtt,
      saveData: connection?.saveData || false,
    };

    setNetworkStatus(newStatus);
    return newStatus;
  }, [networkStatus]);

  useEffect(() => {
    checkNetworkStatus();

    const intervalId = setInterval(() => {
      checkNetworkStatus();
    }, NETWORK_CHECK_INTERVAL);

    const connectionChangeHandler = () => {
      checkNetworkStatus();
    };

    const connection = (navigator as NavigatorWithConnection).connection;
    if (connection) {
      connection.addEventListener('change', connectionChangeHandler);
    }

    return () => {
      clearInterval(intervalId);
      if (connection) {
        connection.removeEventListener('change', connectionChangeHandler);
      }
    };
  }, [checkNetworkStatus]);

  // Return the networkStatus directly instead of wrapping it in an object
  return networkStatus;
}

/**
 * Hook to determine if the current connection is slow
 */
export function useIsSlowConnection(): boolean {
  const status = useNetworkStatus();
  
  return (
    status.effectiveType === 'slow-2g' ||
    status.effectiveType === '2g' ||
    status.effectiveType === '3g' ||
    (status.effectiveType === '4g' && status.downlink && status.downlink < 1.5)
  );
}

/**
 * Hook that returns a simple connection speed label for UI purposes
 */
export function useConnectionSpeed(): 'slow' | 'fast' {
  const isSlowConnection = useIsSlowConnection();
  return isSlowConnection ? 'slow' : 'fast';
}
